import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useApi, api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import useMobile from '../hooks/useMobile';

const COLORS    = ['#7C6FF7','#F7605C','#34D399','#FBBF24','#60A5FA','#F472B6','#A78BFA','#34D399'];
const MONTHS_NO = ['Januar','Februar','Mars','April','Mai','Juni','Juli','August','September','Oktober','November','Desember'];
const DAYS_NO   = ['Man','Tir','Ons','Tor','Fre','Lør','Søn'];

const RANGE_OPTIONS = [
  { value: 7,   label: '7 dager' },
  { value: 14,  label: '14 dager' },
  { value: 30,  label: '30 dager' },
  { value: 90,  label: '3 måneder' },
  { value: 180, label: '6 måneder' },
  { value: 365, label: '1 år' },
];

const LIMIT_OPTIONS = [5, 10, 20, 50];

const GROUP_OPTIONS = [
  { value: 'none',  label: 'Ingen gruppering' },
  { value: 'day',   label: 'Grupper per dag' },
  { value: 'week',  label: 'Grupper per uke' },
  { value: 'month', label: 'Grupper per måned' },
];

const DEFAULT_SETTINGS = { range: 30, limit: 10, group: 'day', hideGoogle: false };

function loadSettings() {
  try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem('calSettings') || '{}') }; }
  catch { return DEFAULT_SETTINGS; }
}
function saveSettings(s) { localStorage.setItem('calSettings', JSON.stringify(s)); }

function weekLabel(date) {
  const d = new Date(date);
  const jan1 = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
  return `Uke ${week}, ${d.getFullYear()}`;
}
function groupLabel(group, date) {
  const d = new Date(date);
  if (group === 'day')   return d.toLocaleDateString('nb-NO', { weekday:'long', day:'numeric', month:'long' });
  if (group === 'week')  return weekLabel(d);
  if (group === 'month') return d.toLocaleDateString('nb-NO', { month:'long', year:'numeric' });
  return null;
}
function groupKey(group, dateStr) {
  const d = new Date(dateStr);
  if (group === 'day')   return d.toISOString().slice(0, 10);
  if (group === 'week')  return weekLabel(d);
  if (group === 'month') return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  return 'all';
}

// ─── Event-detaljer modal ─────────────────────────────────────
function EventModal({ event, onClose, onDelete }) {
  if (!event) return null;
  const start = new Date(event.start_time);
  const end   = event.end_time ? new Date(event.end_time) : null;

  const openMap = (loc) => {
    window.open(`https://maps.google.com/?q=${encodeURIComponent(loc)}`, '_blank');
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        {/* Fargestripe */}
        <div style={{ position:'absolute', top:0, left:0, right:0, height:6, borderRadius:'20px 20px 0 0', background: event.color||'var(--accent)' }} />

        {/* Lukk */}
        <button onClick={onClose} style={s.modalClose}>✕</button>

        <h2 style={s.modalTitle}>{event.title}</h2>

        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {/* Dato og tid */}
          <div style={s.modalRow}>
            <span style={s.modalRowIcon}>📅</span>
            <div>
              <div style={s.modalRowMain}>
                {start.toLocaleDateString('nb-NO', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
              </div>
              {!event.all_day ? (
                <div style={s.modalRowSub}>
                  {start.toLocaleTimeString('nb-NO', { hour:'2-digit', minute:'2-digit' })}
                  {end && ` – ${end.toLocaleTimeString('nb-NO', { hour:'2-digit', minute:'2-digit' })}`}
                  {end && ` (${Math.round((end - start) / 60000)} min)`}
                </div>
              ) : (
                <div style={s.modalRowSub}>Heldagsarrangement</div>
              )}
            </div>
          </div>

          {/* Lokasjon */}
          {event.location && (
            <div style={s.modalRow}>
              <span style={s.modalRowIcon}>📍</span>
              <div style={{ flex:1 }}>
                <div style={s.modalRowMain}>{event.location}</div>
                <button onClick={() => openMap(event.location)} style={s.mapsBtn}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="#4285F4">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  Åpne i Google Maps
                </button>
              </div>
            </div>
          )}

          {/* Beskrivelse */}
          {event.description && (
            <div style={s.modalRow}>
              <span style={s.modalRowIcon}>📝</span>
              <div style={{ fontSize:14, color:'var(--txm)', lineHeight:1.6, flex:1 }}>{event.description}</div>
            </div>
          )}

          {/* Opprettet av */}
          {event.user_name && (
            <div style={s.modalRow}>
              <span style={s.modalRowIcon}>👤</span>
              <div style={{ fontSize:13, color:'var(--txl)' }}>Opprettet av {event.user_name}</div>
            </div>
          )}
        </div>

        {/* Handlinger */}
        <div style={s.modalActions}>
          <button onClick={onClose} style={s.modalSecBtn}>✏️ Lukk</button>
          <button onClick={() => onDelete(event.id)} style={s.modalDelBtn}>🗑️ Slett</button>
        </div>
      </div>
    </div>
  );
}

// ─── iCal-modal ───────────────────────────────────────────────
function ICalModal({ onClose, onAdded }) {
  const [name,    setName]    = useState('');
  const [url,     setUrl]     = useState('');
  const [color,   setColor]   = useState('#7C6FF7');
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const [success, setSuccess] = useState('');

  const normalizeUrl = (raw) => {
    if (!raw) return raw;
    const u = raw.trim();
    if (u.startsWith('webcals://')) return 'https://' + u.slice(10);
    if (u.startsWith('webcal://'))  return 'https://' + u.slice(9);
    if (u.startsWith('icals://'))   return 'https://' + u.slice(8);
    if (u.startsWith('ical://'))    return 'https://' + u.slice(7);
    if (u.startsWith('http://'))    return 'https://' + u.slice(7);
    return u;
  };

  const save = async () => {
    setErr(''); setSuccess('');
    if (!name.trim()) { setErr('Navn er påkrevd'); return; }
    if (!url.trim())  { setErr('URL er påkrevd');  return; }
    const normalizedUrl = normalizeUrl(url);
    setUrl(normalizedUrl);
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/calendar/sources',
        { name: name.trim(), type: 'ical', url: normalizedUrl, color },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(res.data.message || `${name} lagt til!`);
      onAdded();
      setTimeout(() => { onClose(); }, 2000);
    } catch(ex) {
      setErr(ex.response?.data?.error || 'Noe gikk galt – sjekk URL-en');
    } finally { setSaving(false); }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth:440 }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={s.modalClose}>✕</button>
        <h3 style={{ fontSize:17, fontWeight:800, color:'var(--txt)', margin:'0 0 6px' }}>📅 Legg til kalender</h3>
        <p style={{ fontSize:13, color:'var(--txl)', margin:'0 0 16px', lineHeight:1.6 }}>
          Lim inn en iCal/ICS-URL. Fungerer med Itslearning, Apple, Outlook, Google og andre.
        </p>

        {err     && <div style={{ ...s.errBox, marginBottom:10 }}>❌ {err}</div>}
        {success && <div style={{ background:'var(--green-bg)', color:'var(--green)', padding:'10px 14px', borderRadius:9, fontSize:13, marginBottom:10, fontWeight:600 }}>✅ {success}</div>}

        <label style={s.lbl}>Navn på kalenderen</label>
        <input style={{ ...s.inp, marginBottom:10 }} placeholder="F.eks. Itslearning, Jobb"
          value={name} onChange={e => setName(e.target.value)} autoFocus />

        <label style={s.lbl}>iCal URL (.ics eller webcal://)</label>
        <input style={{ ...s.inp, marginBottom:12 }}
          placeholder="webcal://... eller https://..."
          value={url} onChange={e => setUrl(e.target.value)} />

        <div style={{ background:'var(--yellow-bg)', borderRadius:10, padding:'10px 14px', fontSize:12, color:'var(--yellow)', marginBottom:16, lineHeight:1.7 }}>
          💡 <strong>Itslearning:</strong> Gå til Kalender → Eksport → Kopier webcal-lenken<br/>
          <strong>Apple:</strong> Kalender → Del → Offentlig kalender → Kopier lenke<br/>
          <strong>Google:</strong> Kalenderinnstillinger → Integrer → Hemmelig iCal-adresse
        </div>

        <div style={{ display:'flex', gap:8 }}>
          <button onClick={save} disabled={saving || !!success}
            style={{ ...s.saveBtn, flex:1, opacity: saving || success ? 0.7 : 1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            {saving ? (
              <><div style={{ width:13, height:13, border:'2px solid rgba(255,255,255,.4)', borderTop:'2px solid white', borderRadius:'50%', animation:'spin .8s linear infinite' }}/> Legger til og synkroniserer…</>
            ) : '💾 Legg til kalender'}
          </button>
          <button onClick={onClose} style={s.cancelBtn}>Avbryt</button>
        </div>
      </div>
    </div>
  );
}

const calSyncBtn = (color) => ({
  display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:10,
  border:`1px solid ${color}22`, background:`${color}10`, color:'var(--txt)',
  fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit', textAlign:'left',
  width:'100%',
});

export default function CalendarPage() {
  const isMobile = useMobile();
  const { dark } = useTheme();
  const { user } = useAuth();
  const today  = new Date();
  const [year, setYear]           = useState(today.getFullYear());
  const [month, setMonth]         = useState(today.getMonth());
  const [selected, setSelected]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [showICalModal, setShowICalModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [settings, setSettings]   = useState(loadSettings);
  const [form, setForm] = useState({ title:'', start_time:'', end_time:'', color:'#7C6FF7', location:'', all_day:false, description:'' });

  const updateSetting = (key, val) => {
    setSettings(prev => { const next = { ...prev, [key]: val }; saveSettings(next); return next; });
  };

  const from = new Date(year, month, 1).toISOString();
  const to   = new Date(year, month+1, 0, 23, 59).toISOString();
  const { data: monthEvents, refresh: refreshMonth } = useApi(`/api/events?from=${from}&to=${to}`);

  const upcomingFrom = today.toISOString();
  const upcomingTo   = new Date(today.getTime() + settings.range * 86400000).toISOString();
  const { data: upcomingRaw, refresh: refreshUpcoming } = useApi(
    `/api/events?from=${upcomingFrom}&to=${upcomingTo}`, [settings.range]
  );

  const { data: sources, refresh: refreshSources } = useApi('/api/calendar/sources');

  const refresh = () => { refreshMonth(); refreshUpcoming(); };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async e => {
    e.preventDefault();
    await api.post('/api/events', {
      ...form,
      start_time: new Date(form.start_time).toISOString(),
      end_time: form.end_time ? new Date(form.end_time).toISOString() : null,
    });
    setShowForm(false);
    setForm({ title:'', start_time:'', end_time:'', color:'#7C6FF7', location:'', all_day:false, description:'' });
    refresh();
  };

  const del = async id => {
    if (!window.confirm('Slett denne hendelsen?')) return;
    await api.delete(`/api/events/${id}`);
    setSelectedEvent(null);
    refresh();
  };

  const [syncing, setSyncing] = useState({});

  const removeSource = async id => {
    if (!window.confirm('Fjerne denne kalenderkilden?')) return;
    await api.delete(`/api/calendar/sources/${id}`);
    refreshSources();
    refresh();
  };

  const syncSource = async (srcId, name) => {
    setSyncing(s => ({ ...s, [srcId]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `/api/calendar/sources/${srcId}/sync`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refreshSources();
      refresh();
    } catch(e) {
      alert('❌ Synk feilet: ' + (e.response?.data?.error || e.message));
    } finally {
      setSyncing(s => ({ ...s, [srcId]: false }));
    }
  };

  // Kalender-grid
  const firstDay    = new Date(year, month, 1).getDay();
  const offset      = firstDay === 0 ? 6 : firstDay - 1;
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsOnDay = d => {
    if (!d || !monthEvents) return [];
    const dayStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return monthEvents.filter(e => e.start_time?.startsWith(dayStr));
  };

  const isToday   = d => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1); };

  const upcomingEvents = useMemo(() => {
    let evs = upcomingRaw || [];
    if (settings.hideGoogle) evs = evs.filter(e => e.color !== '#4285F4');
    return evs.slice(0, settings.limit);
  }, [upcomingRaw, settings.hideGoogle, settings.limit]);

  const groupedUpcoming = useMemo(() => {
    if (settings.group === 'none') return [{ key:'all', label:null, events:upcomingEvents }];
    const groups = {};
    for (const ev of upcomingEvents) {
      const key = groupKey(settings.group, ev.start_time);
      if (!groups[key]) groups[key] = { key, label:groupLabel(settings.group, ev.start_time), events:[] };
      groups[key].events.push(ev);
    }
    return Object.values(groups);
  }, [upcomingEvents, settings.group]);

  const selectedDayEvents = selected ? eventsOnDay(selected) : [];

  if (isMobile) {
    const selDayLabel = selected
      ? new Date(year,month,selected).toLocaleDateString('nb-NO',{weekday:'long',day:'numeric',month:'long'})
      : null;
    const selCapitalized = selDayLabel
      ? selDayLabel.charAt(0).toUpperCase() + selDayLabel.slice(1)
      : null;
    const pageBg = dark ? '#0A0818' : '#EDE8FF';
    const txt    = dark ? '#E8E8F8' : '#1A1A35';
    const sub    = dark ? '#9090B8' : '#888';
    const border = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';

    return (
      <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif", paddingBottom:16 }}>

        {/* Gradient Hero Header */}
        <div style={{
          background: dark
            ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
            : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
          padding: '52px 20px 24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
          <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
          <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
              <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Kalender</div>
              <div style={{ fontSize:14, color:'rgba(255,255,255,0.7)', marginTop:4 }}>{MONTHS_NO[month]} {year}</div>
            </div>
            <button onClick={()=>setShowForm(v=>!v)}
              style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 16px', fontWeight:700, fontSize:14, cursor:'pointer' }}>
              {showForm ? '✕' : '+ Ny'}
            </button>
          </div>
        </div>

        {/* Ny hendelse form */}
        {showForm && (
          <form onSubmit={save} className="glass-card" style={{ margin:'12px 16px 4px', padding:16, display:'flex', flexDirection:'column', gap:10 }}>
            <input style={{ padding:'12px 14px', borderRadius:12, border:'1px solid '+border, background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none' }} placeholder="Tittel *" value={form.title} onChange={set('title')} autoFocus required/>
            <input type="datetime-local" style={{ padding:'11px 14px', borderRadius:12, border:'1px solid '+border, background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:13, fontFamily:'inherit', outline:'none' }} value={form.start_time} onChange={set('start_time')} required/>
            <input style={{ padding:'12px 14px', borderRadius:12, border:'1px solid '+border, background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none' }} placeholder="Sted (valgfritt)" value={form.location} onChange={set('location')}/>
            <div style={{ display:'flex', gap:8 }}>
              {COLORS.map(c=>(
                <button key={c} type="button" onClick={()=>setForm(f=>({...f,color:c}))} style={{ width:28, height:28, borderRadius:'50%', background:c, border: form.color===c?'3px solid '+txt:'3px solid transparent', cursor:'pointer', flexShrink:0 }}/>
              ))}
            </div>
            <button type="submit" style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:12, padding:13, fontWeight:700, fontSize:15, cursor:'pointer' }}>Lagre hendelse</button>
          </form>
        )}

        {/* Kalender-kort */}
        <div className="glass-card" style={{ margin:'12px 16px', padding:'14px 12px' }}>
          {/* Måned-navigasjon */}
          <div style={{ display:'flex', alignItems:'center', marginBottom:14 }}>
            <button onClick={prevMonth} style={{ background:'none', border:'none', cursor:'pointer', fontSize:24, color:'#7C6FF7', padding:'0 8px', fontWeight:700 }}>‹</button>
            <span style={{ flex:1, textAlign:'center', fontSize:16, fontWeight:700, color:txt }}>{MONTHS_NO[month]} {year}</span>
            <button onClick={nextMonth} style={{ background:'none', border:'none', cursor:'pointer', fontSize:24, color:'#7C6FF7', padding:'0 8px', fontWeight:700 }}>›</button>
          </div>
          {/* Ukedager */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:6 }}>
            {DAYS_NO.map(d => (
              <div key={d} style={{ textAlign:'center', fontSize:12, fontWeight:700, color:sub, paddingBottom:4 }}>{d}</div>
            ))}
          </div>
          {/* Dager */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'2px 0' }}>
            {cells.map((d, i) => {
              const hasEv = eventsOnDay(d).length > 0;
              const isTd  = isToday(d);
              const isSel = selected === d;
              return (
                <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'3px 0' }}
                  onClick={() => d && setSelected(s => s===d ? null : d)}>
                  {d ? (
                    <>
                      <div style={{
                        width:34, height:34, borderRadius:'50%',
                        background: isSel ? 'linear-gradient(135deg,#7C6FF7,#9358E8)' : isTd ? 'rgba(124,111,247,0.15)' : 'transparent',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        cursor:'pointer',
                      }}>
                        <span style={{ fontSize:15, fontWeight: isTd||isSel ? 800 : 400, color: isSel ? '#fff' : isTd ? '#7C6FF7' : txt }}>
                          {d}
                        </span>
                      </div>
                      <div style={{ width:5, height:5, borderRadius:'50%', background: hasEv ? '#7C6FF7' : 'transparent', marginTop:1 }}/>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        {/* Valgt dag / Kommende */}
        <div style={{ padding:'0 16px' }}>
          {selected ? (
            <>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div style={{ fontSize:17, fontWeight:800, color:txt }}>{selCapitalized}</div>
                <button onClick={() => setSelected(null)}
                  style={{ background:'none', border:'none', color:sub, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
                  Vis alle kommende →
                </button>
              </div>
              {selectedDayEvents.length === 0 && (
                <div className="glass-card" style={{ padding:'24px 20px', textAlign:'center', marginBottom:16 }}>
                  <div style={{ fontSize:14, color:sub, marginBottom:8 }}>Ingen hendelser denne dagen</div>
                  <button onClick={()=>setShowForm(true)} style={{ background:'none', border:'none', color:'#7C6FF7', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>+ Legg til hendelse</button>
                </div>
              )}
              {selectedDayEvents.map(ev=>(
                <div key={ev.id} onClick={()=>setSelectedEvent(ev)} className="glass-card"
                  style={{ marginBottom:10, borderLeft:`4px solid ${ev.color||'#7C6FF7'}`, cursor:'pointer', padding:'12px 16px' }}>
                  <div style={{ fontSize:15, fontWeight:700, color:txt, marginBottom:3 }}>{ev.title}</div>
                  <div style={{ fontSize:13, color:sub }}>{ev.all_day ? 'Heldagsarrangement' : new Date(ev.start_time).toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'})}{ev.location&&` · 📍${ev.location}`}</div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div style={{ fontSize:13, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:10 }}>Kommende hendelser</div>
              {upcomingEvents.length===0 && (
                <div className="glass-card" style={{ padding:'32px 20px', textAlign:'center' }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>📅</div>
                  <div style={{ fontSize:14, color:sub, marginBottom:10 }}>Ingen kommende hendelser</div>
                  <button onClick={()=>setShowForm(true)} style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:12, padding:'8px 18px', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>Legg til hendelse</button>
                </div>
              )}
              {upcomingEvents.map(ev=>(
                <div key={ev.id} onClick={()=>setSelectedEvent(ev)} className="glass-card"
                  style={{ marginBottom:10, borderLeft:`4px solid ${ev.color||'#7C6FF7'}`, cursor:'pointer', padding:'12px 16px' }}>
                  <div style={{ fontSize:15, fontWeight:700, color:txt, marginBottom:3 }}>{ev.title}</div>
                  <div style={{ fontSize:13, color:sub }}>
                    {new Date(ev.start_time).toLocaleDateString('nb-NO',{weekday:'short',day:'numeric',month:'short'})} {!ev.all_day && `kl. ${new Date(ev.start_time).toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'})}`}
                    {ev.location&&` · 📍${ev.location}`}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Event modal */}
        {selectedEvent && (
          <EventModal event={selectedEvent} onClose={()=>setSelectedEvent(null)} onDelete={async id=>{await del(id);setSelectedEvent(null);}}/>
        )}
      </div>
    );
  }

  return (
    <div style={s.wrap}>
      {/* Event-modal */}
      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onDelete={async id => { await del(id); setSelectedEvent(null); }}
        />
      )}

      {/* iCal-modal */}
      {showICalModal && (
        <ICalModal
          onClose={() => setShowICalModal(false)}
          onAdded={() => refreshSources()}
        />
      )}

      {/* Venstre: Kalender */}
      <div style={s.calCol}>
        <div style={s.calHeader}>
          <button style={s.navBtn} onClick={prevMonth}>‹</button>
          <span style={s.monthTitle}>{MONTHS_NO[month]} {year}</span>
          <button style={s.navBtn} onClick={nextMonth}>›</button>
        </div>

        <div style={s.calGrid}>
          {DAYS_NO.map(d => <div key={d} style={s.dayLabel}>{d}</div>)}
          {cells.map((d, i) => {
            const dayEvs = eventsOnDay(d);
            return (
              <div key={i}
                style={{...s.cell,
                  ...(isToday(d) ? s.cellToday : {}),
                  ...(selected===d ? s.cellSelected : {}),
                  opacity: d ? 1 : 0,
                  cursor: d ? 'pointer' : 'default',
                }}
                onClick={() => d && setSelected(d === selected ? null : d)}>
                {d && <>
                  <span>{d}</span>
                  <div style={s.dots}>
                    {dayEvs.slice(0,3).map((e,j) => (
                      <div key={j} style={{...s.dot, background: e.color||'#7C6FF7'}} />
                    ))}
                  </div>
                </>}
              </div>
            );
          })}
        </div>

        <button style={s.newBtn} onClick={() => setShowForm(v => !v)}>+ Ny hendelse</button>

        {showForm && (
          <form onSubmit={save} style={s.form}>
            <input style={s.inp} placeholder="Tittel" value={form.title} onChange={set('title')} required autoFocus />
            <input style={s.inp} type="datetime-local" value={form.start_time} onChange={set('start_time')} required />
            <input style={s.inp} type="datetime-local" value={form.end_time} onChange={set('end_time')} />
            <input style={s.inp} placeholder="Sted (valgfritt)" value={form.location} onChange={set('location')} />
            <textarea style={{ ...s.inp, resize:'vertical', minHeight:60 }} placeholder="Beskrivelse (valgfritt)" value={form.description} onChange={set('description')} />
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {COLORS.map(c => (
                <div key={c} onClick={() => setForm(f=>({...f,color:c}))}
                  style={{width:24,height:24,borderRadius:'50%',background:c,cursor:'pointer',
                    border: form.color===c?'3px solid var(--txt)':'3px solid transparent'}} />
              ))}
            </div>
            <div style={{display:'flex',gap:8}}>
              <button style={s.saveBtn} type="submit">Lagre</button>
              <button style={s.cancelBtn} type="button" onClick={() => setShowForm(false)}>Avbryt</button>
            </div>
          </form>
        )}

        {/* Valgt dag */}
        {selected && (
          <div style={s.selectedBox}>
            <div style={s.selectedTitle}>
              {selected}. {MONTHS_NO[month]}
              <button style={s.closeDay} onClick={() => setSelected(null)}>✕</button>
            </div>
            {selectedDayEvents.length === 0
              ? <div style={s.noEv}>Ingen hendelser denne dagen</div>
              : selectedDayEvents.map(ev => (
                <div key={ev.id} style={{...s.evCard, borderLeft:`4px solid ${ev.color||'#7C6FF7'}`, cursor:'pointer'}}
                  onClick={() => setSelectedEvent(ev)}>
                  <div style={s.evName}>{ev.title}</div>
                  {ev.start_time && <div style={s.evMeta}>🕐 {new Date(ev.start_time).toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'})}{ev.end_time?` – ${new Date(ev.end_time).toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'})}`:''}</div>}
                  {ev.location   && <div style={s.evMeta}>📍 {ev.location}</div>}
                  {ev.user_name  && <div style={s.evMeta}>👤 {ev.user_name}</div>}
                </div>
              ))
            }
          </div>
        )}

        {/* Synkroniser-panel */}
        <div style={{ ...s.syncCard, marginTop:14 }}>
          <button style={s.syncToggle} onClick={() => setShowSyncPanel(v => !v)}>
            🔄 Synkroniser kalender {showSyncPanel ? '▲' : '▼'}
          </button>

          {showSyncPanel && (
            <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:8 }}>
              {/* Google Calendar */}
              <button onClick={() => window.location.href = '/api/google/auth?action=connect'}
                style={calSyncBtn('#4285F4')}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google Calendar
              </button>

              <button onClick={() => setShowICalModal(true)} style={calSyncBtn('#555')}>
                🍎 Apple Calendar (iCal URL)
              </button>

              <button onClick={() => setShowICalModal(true)} style={calSyncBtn('#0078D4')}>
                📧 Outlook / Microsoft 365
              </button>

              <button onClick={() => setShowICalModal(true)} style={calSyncBtn('#34D399')}>
                📅 Annen kalender (iCal URL)
              </button>

              {/* Aktive kilder */}
              {sources && sources.length > 0 && (
                <div style={{ marginTop:6 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--txl)', textTransform:'uppercase', letterSpacing:.4, marginBottom:6 }}>
                    Aktive kilder
                  </div>
                  {sources.map(src => (
                    <div key={src.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', background:'var(--bg2)', borderRadius:10, marginBottom:6, border:'1px solid var(--bdr)' }}>
                      <div style={{ width:10, height:10, borderRadius:'50%', background:src.color||'#7C6FF7', flexShrink:0 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'var(--txt)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{src.name}</div>
                        <div style={{ fontSize:10, marginTop:2,
                          color: src.status==='ok' ? 'var(--green)' : src.status==='error' ? 'var(--red)' : 'var(--txl)' }}>
                          {src.status==='ok'    && `✅ ${src.status_message || 'OK'}${src.last_sync ? ' · ' + new Date(src.last_sync).toLocaleString('nb-NO',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}`}
                          {src.status==='error' && `❌ ${src.status_message || 'Synk feilet'}`}
                          {(!src.status || src.status==='untested') && '⏳ Ikke synket ennå'}
                        </div>
                      </div>
                      <button onClick={() => syncSource(src.id, src.name)} disabled={syncing[src.id]}
                        style={{ padding:'4px 10px', borderRadius:7, border:'none', background: syncing[src.id] ? 'var(--bdr)' : 'var(--accent)', color: syncing[src.id] ? 'var(--txl)' : 'white', fontSize:11, fontWeight:700, cursor: syncing[src.id] ? 'not-allowed' : 'pointer', fontFamily:'inherit', flexShrink:0 }}>
                        {syncing[src.id] ? '⏳' : '🔄 Synk'}
                      </button>
                      <button onClick={() => removeSource(src.id)}
                        style={{ background:'none', border:'none', color:'var(--txl)', cursor:'pointer', fontSize:14, padding:'2px 4px', flexShrink:0 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Høyre: Kommende hendelser */}
      <div style={s.evCol}>
        <div style={s.evHeader}>
          <span style={s.evTitle}>Kommende hendelser</span>
          <button style={s.settingsBtn} onClick={() => setShowSettings(v => !v)} title="Innstillinger">⚙️</button>
        </div>

        {showSettings && (
          <div style={s.settingsPanel}>
            <div style={s.settingRow}>
              <label style={s.settingLbl}>Tidshorisont</label>
              <select style={s.sel} value={settings.range}
                onChange={e => updateSetting('range', Number(e.target.value))}>
                {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={s.settingRow}>
              <label style={s.settingLbl}>Maks antall</label>
              <select style={s.sel} value={settings.limit}
                onChange={e => updateSetting('limit', Number(e.target.value))}>
                {LIMIT_OPTIONS.map(o => <option key={o} value={o}>{o} hendelser</option>)}
              </select>
            </div>
            <div style={s.settingRow}>
              <label style={s.settingLbl}>Gruppering</label>
              <select style={s.sel} value={settings.group}
                onChange={e => updateSetting('group', e.target.value)}>
                {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={s.settingRow}>
              <label style={s.settingLbl}>Skjul Google</label>
              <input type="checkbox" checked={settings.hideGoogle}
                onChange={e => updateSetting('hideGoogle', e.target.checked)} />
            </div>
          </div>
        )}

        <div style={s.upcomingMeta}>
          Neste {RANGE_OPTIONS.find(o=>o.value===settings.range)?.label} · {upcomingEvents.length} hendelse{upcomingEvents.length !== 1 ? 'r' : ''}
        </div>

        {upcomingEvents.length === 0 && <div style={s.noEv}>Ingen kommende hendelser i denne perioden</div>}

        {groupedUpcoming.map(group => (
          <div key={group.key}>
            {group.label && <div style={s.groupLabel}>{group.label}</div>}
            {group.events.map(ev => (
              <div key={ev.id} style={{...s.evCard, borderLeft:`4px solid ${ev.color||'#7C6FF7'}`, cursor:'pointer'}}
                onClick={() => setSelectedEvent(ev)}>
                <div style={s.evCardTop}>
                  <div style={s.evName}>{ev.title}</div>
                  <button style={s.delBtn} onClick={e => { e.stopPropagation(); del(ev.id); }}>✕</button>
                </div>
                <div style={s.evMeta}>
                  📅 {new Date(ev.start_time).toLocaleDateString('nb-NO',{weekday:'short',day:'numeric',month:'short'})}
                  {!ev.all_day && ` kl. ${new Date(ev.start_time).toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'})}`}
                  {ev.end_time && ` – ${new Date(ev.end_time).toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'})}`}
                </div>
                {ev.location  && <div style={s.evMeta}>📍 {ev.location}</div>}
                {ev.user_name && <div style={s.evMeta}>👤 {ev.user_name}</div>}
                {ev.color === '#4285F4' && <div style={{...s.evMeta, color:'#4285F4'}}>Google Kalender</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const s = {
  wrap:          { display:'flex', gap:20, flexWrap:'wrap', alignItems:'flex-start' },
  calCol:        { flex:'0 0 340px', minWidth:280 },
  evCol:         { flex:1, minWidth:240 },
  calHeader:     { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 },
  monthTitle:    { fontSize:16, fontWeight:800, color:'var(--txt)' },
  navBtn:        { background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:8, width:32, height:32, cursor:'pointer', fontSize:18, color:'var(--txm)', display:'flex', alignItems:'center', justifyContent:'center' },
  calGrid:       { display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:14 },
  dayLabel:      { fontSize:11, fontWeight:700, color:'var(--txl)', textAlign:'center', padding:'4px 0' },
  cell:          { aspectRatio:'1', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', borderRadius:9, fontSize:13, fontWeight:500, color:'var(--txt)', background:'var(--card)', border:'1px solid var(--bdr)', gap:2 },
  cellToday:     { background:'#7C6FF7', color:'#fff', border:'none', fontWeight:800 },
  cellSelected:  { background:'var(--bdr)', border:'1px solid #7C6FF7' },
  dots:          { display:'flex', gap:2 },
  dot:           { width:4, height:4, borderRadius:'50%' },
  newBtn:        { width:'100%', padding:'10px', borderRadius:10, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, marginBottom:12 },
  form:          { background:'var(--card)', borderRadius:14, padding:14, border:'1px solid var(--bdr)', display:'flex', flexDirection:'column', gap:8, marginBottom:12 },
  inp:           { padding:'9px 12px', borderRadius:9, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)', width:'100%', boxSizing:'border-box' },
  lbl:           { fontSize:11, fontWeight:700, color:'var(--txm)', textTransform:'uppercase', letterSpacing:.5, display:'block', marginBottom:4 },
  saveBtn:       { padding:'9px 20px', borderRadius:9, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  cancelBtn:     { padding:'9px 20px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  selectedBox:   { background:'var(--bg)', borderRadius:14, padding:14, border:'1px solid var(--bdr)', marginTop:4 },
  selectedTitle: { fontSize:14, fontWeight:800, color:'var(--txt)', marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center' },
  closeDay:      { background:'none', border:'none', cursor:'pointer', color:'var(--txl)', fontSize:14, fontWeight:700 },
  syncCard:      { background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:14, padding:14 },
  syncToggle:    { background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, color:'var(--txt)', padding:0, display:'flex', alignItems:'center', gap:8 },
  evHeader:      { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 },
  evTitle:       { fontSize:15, fontWeight:800, color:'var(--txt)' },
  settingsBtn:   { background:'none', border:'none', cursor:'pointer', fontSize:18, padding:'2px 6px', borderRadius:8 },
  settingsPanel: { background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:12, padding:14, marginBottom:12, display:'flex', flexDirection:'column', gap:10 },
  settingRow:    { display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 },
  settingLbl:    { fontSize:13, fontWeight:600, color:'var(--txm)' },
  sel:           { padding:'6px 10px', borderRadius:8, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)', cursor:'pointer' },
  upcomingMeta:  { fontSize:12, color:'var(--txl)', marginBottom:10 },
  groupLabel:    { fontSize:12, fontWeight:800, color:'#7C6FF7', textTransform:'uppercase', letterSpacing:.5, margin:'14px 0 6px', paddingLeft:2 },
  noEv:          { color:'var(--txl)', fontSize:14, padding:'20px 0' },
  evCard:        { background:'var(--card)', borderRadius:12, padding:'12px 14px', marginBottom:6, border:'1px solid var(--bdr)', transition:'box-shadow .15s' },
  evCardTop:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 },
  evName:        { fontSize:14, fontWeight:700, color:'var(--txt)' },
  evMeta:        { fontSize:12, color:'var(--txm)', marginTop:2 },
  delBtn:        { background:'none', border:'none', color:'var(--txl)', cursor:'pointer', fontSize:13, fontWeight:700, padding:0, flexShrink:0 },
  errBox:        { background:'var(--red-bg)', color:'var(--red)', padding:'8px 12px', borderRadius:9, fontSize:13 },

  // Modal
  overlay:       { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20, backdropFilter:'blur(4px)' },
  modal:         { background:'var(--card)', borderRadius:20, padding:28, width:'100%', maxWidth:480, boxShadow:'0 20px 60px rgba(0,0,0,.3)', position:'relative' },
  modalClose:    { position:'absolute', top:16, right:16, background:'var(--bg2)', border:'none', borderRadius:'50%', width:32, height:32, cursor:'pointer', fontSize:16, color:'var(--txm)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit' },
  modalTitle:    { fontSize:20, fontWeight:800, color:'var(--txt)', margin:'12px 0 20px', paddingRight:40 },
  modalRow:      { display:'flex', gap:12, alignItems:'flex-start' },
  modalRowIcon:  { fontSize:18, flexShrink:0, marginTop:1 },
  modalRowMain:  { fontSize:14, fontWeight:600, color:'var(--txt)' },
  modalRowSub:   { fontSize:13, color:'var(--txm)', marginTop:3 },
  mapsBtn:       { display:'flex', alignItems:'center', gap:5, padding:'5px 10px', borderRadius:8, border:'none', background:'#4285F418', color:'#4285F4', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', marginTop:6 },
  modalActions:  { display:'flex', gap:8, marginTop:24, paddingTop:16, borderTop:'1px solid var(--bdr)' },
  modalSecBtn:   { flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--bg2)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  modalDelBtn:   { padding:'10px 16px', borderRadius:10, border:'none', background:'var(--red-bg)', color:'var(--red)', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
};
